import React, { useState } from 'react'
import { 
  TagIcon,
  PlusIcon,
  MagnifyingGlassIcon,
  PencilIcon,
  TrashIcon,
  XMarkIcon,
  CheckCircleIcon,
  XCircleIcon
} from '@heroicons/react/24/outline'
import toast from 'react-hot-toast'
import {
  useGetDiscountCodesQuery,
  useCreateDiscountCodeMutation,
  useUpdateDiscountCodeMutation,
  useDeleteDiscountCodeMutation,
  useValidateDiscountCodeMutation,
  useGetDiscountCodeMetricsQuery,
  useGetDiscountCodeUsageStatsQuery,
  useReactivateDiscountCodeMutation,
  DiscountCode,
  CreateDiscountCodeRequest,
  UpdateDiscountCodeRequest,
  DiscountType,
  DiscountDuration,
} from '../store/api/discountCodesApi'
import { formatDateTimeEST } from '../utils/dateUtils'

interface DiscountCodeFilters {
  page: number;
  limit: number;
  sortBy: string;
  sortOrder: 'ASC' | 'DESC';
  active?: boolean;
  code?: string;
  discountType?: DiscountType;
  expired?: boolean;
}

const DiscountCodes: React.FC = () => {
  const [filters, setFilters] = useState<DiscountCodeFilters>({
    page: 1,
    limit: 20,
    sortBy: 'createdAt',
    sortOrder: 'DESC',
  });

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedCode, setSelectedCode] = useState<DiscountCode | null>(null);
  const [searchCode, setSearchCode] = useState('');

  const { data: codesData, isLoading, error, refetch } = useGetDiscountCodesQuery(filters);
  const { data: metricsData } = useGetDiscountCodeMetricsQuery();
  const [createCode, { isLoading: isCreating }] = useCreateDiscountCodeMutation();
  const [updateCode, { isLoading: isUpdating }] = useUpdateDiscountCodeMutation();
  const [deleteCode, { isLoading: isDeleting }] = useDeleteDiscountCodeMutation();
  const [validateCode, { isLoading: isValidating }] = useValidateDiscountCodeMutation();
  const [reactivateCode, { isLoading: isReactivating }] = useReactivateDiscountCodeMutation();

  const [showValidationModal, setShowValidationModal] = useState(false);
  const [validationResult, setValidationResult] = useState<{
    valid: boolean;
    discountAmount: number;
    finalAmount: number;
    message?: string;
    codeDetails?: DiscountCode;
  } | null>(null);
  const [codeToValidate, setCodeToValidate] = useState('');
  const [expandedCodeId, setExpandedCodeId] = useState<string | null>(null);
  const [showReactivateModal, setShowReactivateModal] = useState(false);
  const [codeToReactivate, setCodeToReactivate] = useState<DiscountCode | null>(null);
  const [newExpirationDate, setNewExpirationDate] = useState<string>('');

  // Form state
  const [formData, setFormData] = useState<CreateDiscountCodeRequest>({
    code: '',
    discountType: 'percentage',
    discountValue: 0,
    currency: 'usd',
    duration: 'once',
    maxRedemptions: undefined,
    expiresAt: undefined,
    firstTimeTransactionOnly: false,
    minimumAmount: undefined,
    useTestMode: false,
  });

  // Handle filter changes
  const handleFilterChange = (key: keyof DiscountCodeFilters, value: any) => {
    setFilters(prev => ({ ...prev, [key]: value, page: 1 }));
  };

  // Handle search
  const handleSearch = (value: string) => {
    setSearchCode(value);
    handleFilterChange('code', value || undefined);
  };

  // Handle create
  const handleCreate = async () => {
    try {
      // Validation
      if (!formData.discountType || formData.discountValue === undefined || formData.discountValue <= 0) {
        toast.error('Please fill in all required fields');
        return;
      }

      if (formData.discountType === 'percentage' && (formData.discountValue < 0 || formData.discountValue > 100)) {
        toast.error('Percentage must be between 0 and 100');
        return;
      }

      if (formData.discountType === 'fixed_amount' && !formData.currency) {
        toast.error('Currency is required for fixed amount discounts');
        return;
      }

      if (formData.duration === 'repeating' && !formData.durationInMonths) {
        toast.error('Duration in months is required for repeating discounts');
        return;
      }

      // Validate expiration date if provided
      if (formData.expiresAt) {
        const expirationDate = new Date(formData.expiresAt);
        const now = new Date();
        if (expirationDate.getTime() <= now.getTime() + 120000) {
          toast.error('Expiration date must be at least 2 minutes in the future');
          return;
        }
      }

      await createCode(formData).unwrap();
      toast.success('Discount code created successfully');
      setShowCreateModal(false);
      resetForm();
      refetch();
    } catch (error: any) {
      toast.error(error?.data?.error || error?.message || 'Failed to create discount code');
    }
  };

  // Handle update
  const handleUpdate = async () => {
    if (!selectedCode) return;

    try {
      // Only active can be updated in Stripe
      const updateData: UpdateDiscountCodeRequest = {
        active: (formData as any).active !== undefined ? (formData as any).active : selectedCode.active,
      };

      await updateCode({ id: selectedCode.id, data: updateData }).unwrap();
      toast.success('Discount code updated successfully');
      setShowEditModal(false);
      setSelectedCode(null);
      resetForm();
      refetch();
    } catch (error: any) {
      toast.error(error?.data?.error || error?.message || 'Failed to update discount code');
    }
  };

  // Handle delete
  const handleDelete = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this discount code? It will be deactivated.')) {
      return;
    }

    try {
      await deleteCode(id).unwrap();
      toast.success('Discount code deleted successfully');
      refetch();
    } catch (error: any) {
      toast.error(error?.data?.error || error?.message || 'Failed to delete discount code');
    }
  };

  // Handle validate
  const handleValidate = async (code: string) => {
    try {
      setCodeToValidate(code);
      setShowValidationModal(true);
      setValidationResult(null);

      // Call validation endpoint with just the code string
      const result = await validateCode(code).unwrap();
      // The transformResponse already extracts the data, so result is the validation object
      setValidationResult(result);
      
      // Show toast notification
      if (result.valid) {
        toast.success(`Code '${code}' is valid!`);
      } else {
        toast.error(`Code '${code}' is invalid: ${result.message || 'Unknown error'}`);
      }
    } catch (error: any) {
      const errorMessage = error?.data?.error || error?.data?.message || error?.message || 'Failed to validate code';
      setValidationResult({
        valid: false,
        discountAmount: 0,
        finalAmount: 39,
        message: errorMessage,
      });
      toast.error(`Validation failed: ${errorMessage}`);
    }
  };

  // Reset form
  const resetForm = () => {
    setFormData({
      code: '',
      discountType: 'percentage',
      discountValue: 0,
      currency: 'usd',
      duration: 'once',
      maxRedemptions: undefined,
      expiresAt: undefined,
      firstTimeTransactionOnly: false,
      minimumAmount: undefined,
      useTestMode: false,
    });
  };

  // Open edit modal
  const openEditModal = (code: DiscountCode) => {
    if (!code || !code.id) {
      toast.error('Invalid code data');
      return;
    }
    setSelectedCode(code);
    setFormData({
      code: code.code || '',
      discountType: code.discountType || 'percentage',
      discountValue: code.discountValue || 0,
      currency: code.currency || 'usd',
      duration: code.duration || 'once',
      durationInMonths: code.durationInMonths,
      maxRedemptions: code.maxRedemptions,
      expiresAt: code.expiresAt,
      firstTimeTransactionOnly: code.firstTimeTransactionOnly || false,
      minimumAmount: code.minimumAmount,
      active: code.active !== undefined ? code.active : true,
    } as any);
    setShowEditModal(true);
  };

  // Format discount value
  const formatDiscountValue = (code: DiscountCode) => {
    if (!code || !code.discountType || code.discountValue === undefined || code.discountValue === null) {
      return '—';
    }
    if (code.discountType === 'percentage') {
      return `${code.discountValue}%`;
    }
    const amount = typeof code.discountValue === 'number' ? code.discountValue : 0;
    const currency = code.currency?.toUpperCase() || 'USD';
    return `$${(amount / 100).toFixed(2)} ${currency}`;
  };

  // Format date+time in Eastern Time (server sends UTC without timezone)
  const formatDate = (dateString?: string) => (dateString ? formatDateTimeEST(dateString) : '—');

  // Helper function to check if code is expired (considers codes expiring today as expired)
  const isCodeExpired = (expiresAt?: string): boolean => {
    if (!expiresAt) return false;
    try {
      const expirationDate = new Date(expiresAt);
      if (isNaN(expirationDate.getTime())) return false;
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      expirationDate.setHours(0, 0, 0, 0);
      return expirationDate <= today;
    } catch (e) {
      return false;
    }
  };

  // Get status badge
  const getStatusBadge = (code: DiscountCode) => {
    if (!code.active) {
      return <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-gray-100 text-gray-800">Inactive</span>;
    }
    if (isCodeExpired(code.expiresAt)) {
      return <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-800">Expired</span>;
    }
    if (code.maxRedemptions && code.timesRedeemed >= code.maxRedemptions) {
      return <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-yellow-100 text-yellow-800">Max Uses Reached</span>;
    }
    return <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">Active</span>;
  };

  if (isLoading && !codesData) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Discount Codes</h1>
          <p className="text-gray-600 mt-2">Manage discount codes and promotions</p>
        </div>
        <button
          onClick={() => {
            resetForm();
            setShowCreateModal(true);
          }}
          className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
        >
          <PlusIcon className="h-5 w-5 mr-2" />
          Create Code
        </button>
      </div>

      {/* Metrics Dashboard */}
      {metricsData?.data && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Total Codes */}
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Codes</p>
                <p className="text-3xl font-bold text-gray-900 mt-2">{metricsData.data.totalCodes}</p>
                <p className="text-xs text-gray-500 mt-1">
                  {metricsData.data.activeCodes} active
                </p>
              </div>
              <div className="bg-primary-100 rounded-full p-3">
                <TagIcon className="h-6 w-6 text-primary-600" />
              </div>
            </div>
          </div>

          {/* Total Redemptions */}
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Redemptions</p>
                <p className="text-3xl font-bold text-gray-900 mt-2">{metricsData.data.totalRedemptions}</p>
                <p className="text-xs text-gray-500 mt-1">
                  Avg: {metricsData.data.averageRedemptionsPerCode.toFixed(1)} per code
                </p>
              </div>
              <div className="bg-green-100 rounded-full p-3">
                <CheckCircleIcon className="h-6 w-6 text-green-600" />
              </div>
            </div>
          </div>

          {/* Total Discount Given */}
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Discount Given</p>
                <p className="text-3xl font-bold text-gray-900 mt-2">
                  ${(metricsData.data.totalDiscountAmount / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  Across all codes
                </p>
              </div>
              <div className="bg-yellow-100 rounded-full p-3">
                <TagIcon className="h-6 w-6 text-yellow-600" />
              </div>
            </div>
          </div>

          {/* Usage Rate */}
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Usage Rate</p>
                <p className="text-3xl font-bold text-gray-900 mt-2">
                  {metricsData.data.totalCodes > 0 
                    ? Math.round((metricsData.data.usedCodes / metricsData.data.totalCodes) * 100)
                    : 0}%
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  {metricsData.data.usedCodes} of {metricsData.data.totalCodes} used
                </p>
              </div>
              <div className="bg-blue-100 rounded-full p-3">
                <CheckCircleIcon className="h-6 w-6 text-blue-600" />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Additional Metrics */}
      {metricsData?.data && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {/* Status Breakdown */}
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Status Breakdown</h3>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Active</span>
                <span className="text-sm font-semibold text-green-600">{metricsData.data.codesByStatus.active}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Inactive</span>
                <span className="text-sm font-semibold text-gray-600">{metricsData.data.codesByStatus.inactive}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Expired</span>
                <span className="text-sm font-semibold text-red-600">{metricsData.data.codesByStatus.expired}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Max Uses Reached</span>
                <span className="text-sm font-semibold text-orange-600">{metricsData.data.codesByStatus.maxUsesReached}</span>
              </div>
            </div>
          </div>

          {/* Type Breakdown */}
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Type Breakdown</h3>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Percentage</span>
                <span className="text-sm font-semibold text-primary-600">{metricsData.data.codesByType.percentage}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Fixed Amount</span>
                <span className="text-sm font-semibold text-primary-600">{metricsData.data.codesByType.fixed_amount}</span>
              </div>
            </div>
          </div>

          {/* Recent Activity */}
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Recent Activity</h3>
            <div className="space-y-3">
              <div>
                <p className="text-sm text-gray-600">Codes Created (7 days)</p>
                <p className="text-2xl font-bold text-gray-900">{metricsData.data.recentActivity.codesCreatedLast7Days}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Codes Created (30 days)</p>
                <p className="text-2xl font-bold text-gray-900">{metricsData.data.recentActivity.codesCreatedLast30Days}</p>
              </div>
              {metricsData.data.mostUsedCode && (
                <div className="pt-3 border-t border-gray-200">
                  <p className="text-sm text-gray-600">Most Used Code</p>
                  <p className="text-lg font-semibold text-gray-900">{metricsData.data.mostUsedCode.code}</p>
                  <p className="text-xs text-gray-500">{metricsData.data.mostUsedCode.redemptions} redemptions</p>
                  {metricsData.data.mostUsedCode.discountGiven && (
                    <p className="text-xs text-green-600">${(metricsData.data.mostUsedCode.discountGiven / 100).toFixed(2)} discount given</p>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Advanced Analytics Section */}
      {metricsData?.data && (metricsData.data.performance || metricsData.data.alerts || metricsData.data.efficiency) && (
        <div className="space-y-6">
          {/* Alerts Section */}
          {metricsData.data.alerts && (
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-4">⚠️ Alerts & Recommendations</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {metricsData.data.alerts?.expiringToday && metricsData.data.alerts.expiringToday.length > 0 && (
                  <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 col-span-full">
                    <h3 className="text-sm font-semibold text-orange-900 mb-2">Expires Today ({metricsData.data.alerts.expiringToday.length})</h3>
                    <div className="space-y-3">
                      {metricsData.data.alerts.expiringToday.slice(0, 5).map((alert: {
                        code: string;
                        codeId: string;
                        expiresAt: string;
                        discountValue: number;
                        discountType: 'percentage' | 'fixed_amount';
                        timesRedeemed: number;
                        maxRedemptions?: number;
                      }) => {
                          if (!alert || !alert.codeId) return null;
                          try {
                            const expiresDate = alert.expiresAt ? new Date(alert.expiresAt) : null;
                            const discountDisplay = alert.discountType === 'percentage' 
                              ? `${alert.discountValue || 0}%` 
                              : `$${((alert.discountValue || 0) / 100).toFixed(2)}`;
                            return (
                              <div key={alert.codeId} className="p-3 bg-white rounded-lg border border-orange-200">
                                <div className="flex items-center gap-2 mb-1">
                                  <span className="font-semibold text-orange-900">{alert.code || 'Unknown'}</span>
                                  <span className="text-xs text-orange-600">({discountDisplay})</span>
                                </div>
                                <div className="text-xs text-orange-700">
                                  {expiresDate && !isNaN(expiresDate.getTime()) ? (
                                    <>
                                      Expires: {expiresDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })} at {expiresDate.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}
                                    </>
                                  ) : (
                                    'Expires: Invalid date'
                                  )}
                                  {alert.maxRedemptions && (
                                    <span className="ml-2">• Used: {alert.timesRedeemed || 0}/{alert.maxRedemptions}</span>
                                  )}
                                </div>
                              </div>
                            );
                          } catch (e) {
                            return (
                              <div key={alert.codeId || 'error'} className="p-3 bg-white rounded-lg border border-orange-200 text-xs text-red-600">
                                Error displaying code information
                              </div>
                            );
                          }
                        })}
                    </div>
                  </div>
                )}
                {metricsData.data.alerts.expiringSoon && metricsData.data.alerts.expiringSoon.length > 0 && (
                  <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                    <h3 className="text-sm font-semibold text-orange-900 mb-2">Expiring Soon ({metricsData.data.alerts.expiringSoon.length})</h3>
                    <div className="space-y-2">
                      {metricsData.data.alerts.expiringSoon.slice(0, 3).map((alert) => (
                        <div key={alert.codeId} className="text-xs text-orange-800">
                          <span className="font-medium">{alert.code}</span> - {alert.daysUntilExpiry} days left
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {metricsData.data.alerts.nearMaxUses && metricsData.data.alerts.nearMaxUses.length > 0 && (
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                    <h3 className="text-sm font-semibold text-yellow-900 mb-2">Near Max Uses ({metricsData.data.alerts.nearMaxUses.length})</h3>
                    <div className="space-y-2">
                      {metricsData.data.alerts.nearMaxUses.slice(0, 3).map((alert) => (
                        <div key={alert.codeId} className="text-xs text-yellow-800">
                          <span className="font-medium">{alert.code}</span> - {alert.usagePercentage}% used
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {metricsData.data.alerts.inactiveHighValue && metricsData.data.alerts.inactiveHighValue.length > 0 && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <h3 className="text-sm font-semibold text-red-900 mb-2">Inactive High Value ({metricsData.data.alerts.inactiveHighValue.length})</h3>
                    <div className="space-y-2">
                      {metricsData.data.alerts.inactiveHighValue.slice(0, 3).map((alert) => (
                        <div key={alert.codeId} className="text-xs text-red-800">
                          <span className="font-medium">{alert.code}</span> - {alert.discountType === 'percentage' ? `${alert.discountValue}%` : `$${(alert.discountValue / 100).toFixed(2)}`}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {metricsData.data.alerts.expiredCodes && metricsData.data.alerts.expiredCodes.length > 0 && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4 col-span-full">
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="text-sm font-semibold text-red-900">Expired Codes ({metricsData.data.alerts.expiredCodes.length})</h3>
                    </div>
                    <div className="space-y-3">
                      {metricsData.data.alerts.expiredCodes.slice(0, 5).map((alert) => {
                          if (!alert || !alert.codeId) return null;
                          try {
                            const expiredDate = alert.expiresAt ? new Date(alert.expiresAt) : null;
                            const discountDisplay = alert.discountType === 'percentage' 
                              ? `${alert.discountValue || 0}%` 
                              : `$${((alert.discountValue || 0) / 100).toFixed(2)}`;
                            return (
                              <div key={alert.codeId} className="flex items-center justify-between p-3 bg-white rounded-lg border border-red-200">
                                <div className="flex-1">
                                  <div className="flex items-center gap-2 mb-1">
                                    <span className="font-semibold text-red-900">{alert.code || 'Unknown'}</span>
                                    <span className="text-xs text-red-600">({discountDisplay})</span>
                                  </div>
                                  <div className="text-xs text-red-700">
                                    {expiredDate && !isNaN(expiredDate.getTime()) ? (
                                      <>Expired: {expiredDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</>
                                    ) : (
                                      'Expired: Invalid date'
                                    )}
                                    {alert.maxRedemptions && (
                                      <span className="ml-2">• Used: {alert.timesRedeemed || 0}/{alert.maxRedemptions}</span>
                                    )}
                                  </div>
                                </div>
                                <div className="flex items-center gap-2">
                                <button
                                  onClick={async () => {
                                    try {
                                      // Find the code in the list to get full details
                                      const code = codesData?.data?.find(c => c.id === alert.codeId);
                                      if (!code) {
                                        toast.error('Code not found. Please refresh the page.');
                                        return;
                                      }
                                      
                                      // Check if expiration date has passed
                                      if (code.expiresAt) {
                                        const expiresDate = new Date(code.expiresAt);
                                        const now = new Date();
                                        
                                        // Validate date
                                        if (isNaN(expiresDate.getTime())) {
                                          toast.error('Invalid expiration date. Please contact support.');
                                          return;
                                        }
                                        
                                        if (expiresDate <= now) {
                                          // Show modal to reconfigure expiration date
                                          setCodeToReactivate(code);
                                          // Set default to tomorrow at end of day
                                          const tomorrow = new Date();
                                          tomorrow.setDate(tomorrow.getDate() + 1);
                                          tomorrow.setHours(23, 59, 0, 0);
                                          setNewExpirationDate(tomorrow.toISOString().slice(0, 16));
                                          setShowReactivateModal(true);
                                          return;
                                        }
                                        
                                        // Expiration date is still valid, just reactivate
                                        await updateCode({ 
                                          id: alert.codeId, 
                                          data: { active: true } 
                                        }).unwrap();
                                        toast.success(`Code "${alert.code}" has been reactivated. It will expire on ${expiresDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}.`);
                                        refetch();
                                      } else {
                                        // No expiration date, just reactivate
                                        await updateCode({ 
                                          id: alert.codeId, 
                                          data: { active: true } 
                                        }).unwrap();
                                        toast.success(`Code "${alert.code}" has been reactivated.`);
                                        refetch();
                                      }
                                    } catch (error: any) {
                                      toast.error(error?.data?.error || error?.message || 'Failed to reactivate code');
                                    }
                                  }}
                                  className="px-3 py-1 text-xs font-medium text-white bg-green-600 hover:bg-green-700 rounded-md transition-colors"
                                >
                                  Reactivate
                                </button>
                              </div>
                            </div>
                          );
                          } catch (e) {
                            return (
                              <div key={alert.codeId || 'error'} className="p-3 bg-white rounded-lg border border-red-200 text-xs text-red-600">
                                Error displaying code information
                              </div>
                            );
                          }
                        })}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Performance Section */}
          {metricsData.data.performance && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Top Performers */}
              {metricsData.data.performance.topPerformers && metricsData.data.performance.topPerformers.length > 0 && (
                <div className="bg-white rounded-lg border border-gray-200 p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">🏆 Top Performers</h3>
                  <div className="space-y-3">
                    {metricsData.data.performance.topPerformers.map((performer, idx) => (
                      <div key={performer.codeId} className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                        <div className="flex items-center gap-3">
                          <span className="text-lg font-bold text-green-600">#{idx + 1}</span>
                          <div>
                            <p className="font-semibold text-gray-900">{performer.code}</p>
                            <p className="text-xs text-gray-600">{performer.redemptions} redemptions</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-sm font-semibold text-green-600">${(performer.discountGiven / 100).toFixed(2)}</p>
                          <p className="text-xs text-gray-500">discount given</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Underperformers */}
              {metricsData.data.performance.underperformers && metricsData.data.performance.underperformers.length > 0 && (
                <div className="bg-white rounded-lg border border-gray-200 p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">📉 Underperformers</h3>
                  <div className="space-y-3">
                    {metricsData.data.performance.underperformers.map((underperformer) => (
                      <div key={underperformer.codeId} className="flex items-center justify-between p-3 bg-orange-50 rounded-lg">
                        <div>
                          <p className="font-semibold text-gray-900">{underperformer.code}</p>
                          <p className="text-xs text-gray-600">{underperformer.redemptions} redemptions in {underperformer.daysSinceCreation} days</p>
                        </div>
                        <span className="text-xs text-orange-600 font-medium">Low usage</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Efficiency & Financial Metrics */}
          {metricsData.data.efficiency && metricsData.data.financial && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Efficiency Metrics */}
              <div className="bg-white rounded-lg border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">⚡ Efficiency Metrics</h3>
                <div className="space-y-4">
                  <div>
                    <p className="text-sm text-gray-600">Conversion Rate</p>
                    <p className="text-3xl font-bold text-primary-600">{metricsData.data.efficiency.conversionRate.toFixed(1)}%</p>
                    <p className="text-xs text-gray-500">Codes that have been used</p>
                  </div>
                  <div className="grid grid-cols-2 gap-4 pt-4 border-t">
                    <div>
                      <p className="text-sm text-gray-600">Avg. Discount/Code</p>
                      <p className="text-xl font-semibold text-gray-900">${(metricsData.data.efficiency.averageDiscountPerCode / 100).toFixed(2)}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Avg. Discount/Use</p>
                      <p className="text-xl font-semibold text-gray-900">${(metricsData.data.efficiency.averageDiscountPerRedemption / 100).toFixed(2)}</p>
                    </div>
                  </div>
                  {metricsData.data.efficiency.codesByDuration && (
                    <div className="pt-4 border-t">
                      <p className="text-sm font-semibold text-gray-700 mb-2">By Duration</p>
                      <div className="space-y-2 text-xs">
                        <div className="flex justify-between">
                          <span>Once:</span>
                          <span className="font-medium">{metricsData.data.efficiency.codesByDuration.once.count} codes, {metricsData.data.efficiency.codesByDuration.once.avgRedemptions.toFixed(1)} avg uses</span>
                        </div>
                        <div className="flex justify-between">
                          <span>Repeating:</span>
                          <span className="font-medium">{metricsData.data.efficiency.codesByDuration.repeating.count} codes, {metricsData.data.efficiency.codesByDuration.repeating.avgRedemptions.toFixed(1)} avg uses</span>
                        </div>
                        <div className="flex justify-between">
                          <span>Forever:</span>
                          <span className="font-medium">{metricsData.data.efficiency.codesByDuration.forever.count} codes, {metricsData.data.efficiency.codesByDuration.forever.avgRedemptions.toFixed(1)} avg uses</span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Financial Metrics */}
              <div className="bg-white rounded-lg border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">💰 Financial Impact</h3>
                <div className="space-y-4">
                  <div>
                    <p className="text-sm text-gray-600">Total Discount Given</p>
                    <p className="text-3xl font-bold text-green-600">${(metricsData.data.financial.totalDiscountGiven / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Avg. Discount %</p>
                    <p className="text-2xl font-semibold text-gray-900">{metricsData.data.financial.averageDiscountPercentage.toFixed(1)}%</p>
                  </div>
                  {metricsData.data.financial.discountDistribution && (
                    <div className="pt-4 border-t">
                      <p className="text-sm font-semibold text-gray-700 mb-3">Discount Distribution</p>
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <span className="text-xs text-gray-600">&lt; 10%</span>
                          <div className="flex-1 mx-2 bg-gray-200 rounded-full h-2">
                            <div className="bg-blue-500 h-2 rounded-full" style={{ width: `${(metricsData.data.financial.discountDistribution.under10 / metricsData.data.totalCodes) * 100}%` }}></div>
                          </div>
                          <span className="text-xs font-medium">{metricsData.data.financial.discountDistribution.under10}</span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-xs text-gray-600">10-25%</span>
                          <div className="flex-1 mx-2 bg-gray-200 rounded-full h-2">
                            <div className="bg-green-500 h-2 rounded-full" style={{ width: `${(metricsData.data.financial.discountDistribution.between10and25 / metricsData.data.totalCodes) * 100}%` }}></div>
                          </div>
                          <span className="text-xs font-medium">{metricsData.data.financial.discountDistribution.between10and25}</span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-xs text-gray-600">25-50%</span>
                          <div className="flex-1 mx-2 bg-gray-200 rounded-full h-2">
                            <div className="bg-yellow-500 h-2 rounded-full" style={{ width: `${(metricsData.data.financial.discountDistribution.between25and50 / metricsData.data.totalCodes) * 100}%` }}></div>
                          </div>
                          <span className="text-xs font-medium">{metricsData.data.financial.discountDistribution.between25and50}</span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-xs text-gray-600">&gt; 50%</span>
                          <div className="flex-1 mx-2 bg-gray-200 rounded-full h-2">
                            <div className="bg-red-500 h-2 rounded-full" style={{ width: `${(metricsData.data.financial.discountDistribution.over50 / metricsData.data.totalCodes) * 100}%` }}></div>
                          </div>
                          <span className="text-xs font-medium">{metricsData.data.financial.discountDistribution.over50}</span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Unused Codes */}
          {metricsData.data.performance?.unusedCodes && metricsData.data.performance.unusedCodes.length > 0 && (
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">🚫 Unused Codes ({metricsData.data.performance.unusedCodes.length})</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-3">
                {metricsData.data.performance.unusedCodes.slice(0, 10).map((unused) => (
                  <div key={unused.codeId} className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                    <p className="font-mono text-sm font-semibold text-gray-900">{unused.code}</p>
                    <p className="text-xs text-gray-500 mt-1">{unused.daysSinceCreation} days old</p>
                    {unused.expiresAt && (
                      <p className="text-xs text-orange-600 mt-1">
                        Expires: {formatDateTimeEST(unused.expiresAt)}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search by code..."
                value={searchCode}
                onChange={(e) => handleSearch(e.target.value)}
                className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
          </div>
          <select
            value={filters.discountType || ''}
            onChange={(e) => handleFilterChange('discountType', e.target.value || undefined)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
          >
            <option value="">All Types</option>
            <option value="percentage">Percentage</option>
            <option value="fixed_amount">Fixed Amount</option>
          </select>
          <select
            value={filters.active === undefined ? '' : filters.active.toString()}
            onChange={(e) => handleFilterChange('active', e.target.value === '' ? undefined : e.target.value === 'true')}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
          >
            <option value="">All Status</option>
            <option value="true">Active</option>
            <option value="false">Inactive</option>
          </select>
          <select
            value={filters.expired === undefined ? '' : filters.expired.toString()}
            onChange={(e) => handleFilterChange('expired', e.target.value === '' ? undefined : e.target.value === 'true')}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
          >
            <option value="">All Expiration</option>
            <option value="true">Expired</option>
            <option value="false">Not Expired</option>
          </select>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center h-32">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
          </div>
        ) : error ? (
          <div className="text-center py-12">
            <div className="text-red-400 mb-4">
              <TagIcon className="mx-auto h-12 w-12" />
            </div>
            <h3 className="text-sm font-medium text-gray-900 mb-1">Error loading discount codes</h3>
            <p className="text-sm text-gray-500">Please try again later</p>
          </div>
        ) : !codesData?.data || codesData.data.length === 0 ? (
          <div className="text-center py-12">
            <div className="text-gray-400 mb-4">
              <TagIcon className="mx-auto h-12 w-12" />
            </div>
            <h3 className="text-sm font-medium text-gray-900 mb-1">No discount codes found</h3>
            <p className="text-sm text-gray-500">Create your first discount code to get started</p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Code
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Discount
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Duration
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Usage
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Expires
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Created
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {codesData.data.map((code) => {
                    const expired = isCodeExpired(code.expiresAt);
                    return (
                    <React.Fragment key={code.id}>
                      <tr className={`hover:bg-gray-50 ${expired ? 'bg-red-50' : ''}`}>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">{code.code}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">{formatDiscountValue(code)}</div>
                          <div className="text-xs text-gray-500">{code.discountType === 'percentage' ? 'Percentage' : 'Fixed Amount'}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {code.duration === 'once' ? 'Once' : code.duration === 'repeating' ? `${code.durationInMonths || 0} months` : 'Forever'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center gap-2">
                            <div className="flex-1 min-w-[100px]">
                              <div className="flex items-center justify-between text-xs text-gray-600 mb-1">
                                <span>
                                  {code.trackedRedemptions !== undefined 
                                    ? `${code.trackedRedemptions} tracked` 
                                    : `${code.timesRedeemed || 0} Stripe`} / {code.maxRedemptions || '∞'}
                                </span>
                                {code.maxRedemptions && code.maxRedemptions > 0 && (
                                  <span className="text-gray-500">
                                    {Math.round(((code.trackedRedemptions ?? code.timesRedeemed ?? 0) / code.maxRedemptions) * 100)}%
                                  </span>
                                )}
                              </div>
                              {code.trackedRedemptions !== undefined && code.trackedRedemptions > 0 && (
                                <div className="text-xs text-blue-600 mb-1">
                                  {code.trackedRedemptions} user{code.trackedRedemptions !== 1 ? 's' : ''} used this code
                                </div>
                              )}
                              {code.maxRedemptions && code.maxRedemptions > 0 ? (
                                <div className="w-full bg-gray-200 rounded-full h-2">
                                  <div
                                    className={`h-2 rounded-full ${
                                      (code.trackedRedemptions ?? code.timesRedeemed ?? 0) >= code.maxRedemptions
                                        ? 'bg-red-500'
                                        : (code.trackedRedemptions ?? code.timesRedeemed ?? 0) >= code.maxRedemptions * 0.8
                                        ? 'bg-yellow-500'
                                        : 'bg-green-500'
                                    }`}
                                    style={{ width: `${Math.min(100, Math.max(0, ((code.trackedRedemptions ?? code.timesRedeemed ?? 0) / code.maxRedemptions) * 100))}%` }}
                                  />
                                </div>
                              ) : (
                                <div className="w-full bg-gray-200 rounded-full h-2">
                                  <div
                                    className="h-2 rounded-full bg-blue-500"
                                    style={{ width: (code.trackedRedemptions ?? code.timesRedeemed ?? 0) > 0 ? '100%' : '0%' }}
                                  />
                                </div>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {getStatusBadge(code)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {formatDate(code.expiresAt)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {formatDate(code.createdAt)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <div className="flex items-center justify-end gap-2">
                            <button
                              onClick={() => setExpandedCodeId(expandedCodeId === code.id ? null : code.id)}
                              className="text-blue-600 hover:text-blue-900"
                              title="View detailed stats"
                            >
                              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                {expandedCodeId === code.id ? (
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                ) : (
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                )}
                              </svg>
                            </button>
                            <button
                              onClick={() => handleValidate(code.code)}
                              disabled={isValidating}
                              className="text-green-600 hover:text-green-900 disabled:opacity-50"
                              title="Validate code"
                            >
                              <CheckCircleIcon className="h-5 w-5" />
                            </button>
                            <button
                              onClick={() => openEditModal(code)}
                              className="text-primary-600 hover:text-primary-900"
                              title="Edit code"
                            >
                              <PencilIcon className="h-5 w-5" />
                            </button>
                            <button
                              onClick={() => handleDelete(code.id)}
                              disabled={isDeleting}
                              className="text-red-600 hover:text-red-900 disabled:opacity-50"
                              title="Delete code"
                            >
                              <TrashIcon className="h-5 w-5" />
                            </button>
                          </div>
                        </td>
                      </tr>
                      {expandedCodeId === code.id && (
                        <tr>
                          <td colSpan={8} className="px-6 py-4 bg-gray-50">
                            <CodeStatsRow codeId={code.id} code={code} />
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {codesData?.meta && codesData.meta.totalPages > 1 && (
              <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
                <div className="flex-1 flex justify-between sm:hidden">
                  <button
                    onClick={() => handleFilterChange('page', Math.max(1, (filters.page || 1) - 1))}
                    disabled={(filters.page || 1) === 1}
                    className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => handleFilterChange('page', Math.min(codesData.meta.totalPages, (filters.page || 1) + 1))}
                    disabled={(filters.page || 1) >= codesData.meta.totalPages}
                    className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Next
                  </button>
                </div>
                <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                  <div>
                    <p className="text-sm text-gray-700">
                      Showing <span className="font-medium">{(filters.page - 1) * filters.limit + 1}</span> to{' '}
                      <span className="font-medium">
                        {Math.min(filters.page * filters.limit, codesData.meta.total)}
                      </span>{' '}
                      of <span className="font-medium">{codesData.meta.total}</span> results
                    </p>
                  </div>
                  <div>
                    <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                      <button
                        onClick={() => handleFilterChange('page', Math.max(1, filters.page - 1))}
                        disabled={filters.page === 1}
                        className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Previous
                      </button>
                      <button
                        onClick={() => handleFilterChange('page', Math.min(codesData.meta.totalPages, filters.page + 1))}
                        disabled={filters.page >= codesData.meta.totalPages}
                        className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Next
                      </button>
                    </nav>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Create Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50" onClick={() => setShowCreateModal(false)}>
          <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-2xl shadow-lg rounded-md bg-white" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">Create Discount Code</h3>
              <button
                onClick={() => {
                  setShowCreateModal(false);
                  resetForm();
                }}
                className="text-gray-400 hover:text-gray-500"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Code (optional, auto-generated if empty)</label>
                <input
                  type="text"
                  value={formData.code || ''}
                  onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                  placeholder="e.g., SUMMER2024"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Discount Type *</label>
                  <select
                    value={formData.discountType}
                    onChange={(e) => setFormData({ ...formData, discountType: e.target.value as DiscountType })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  >
                    <option value="percentage">Percentage</option>
                    <option value="fixed_amount">Fixed Amount</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Discount Value * {formData.discountType === 'percentage' ? '(0-100)' : '(in cents, e.g., 5000 = $50)'}
                  </label>
                  <input
                    type="number"
                    value={formData.discountValue || ''}
                    onChange={(e) => setFormData({ ...formData, discountValue: parseFloat(e.target.value) || 0 })}
                    min={0}
                    max={formData.discountType === 'percentage' ? 100 : undefined}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  />
                </div>
              </div>

              {formData.discountType === 'fixed_amount' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Currency *</label>
                  <select
                    value={formData.currency || 'usd'}
                    onChange={(e) => setFormData({ ...formData, currency: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  >
                    <option value="usd">USD</option>
                    <option value="eur">EUR</option>
                    <option value="gbp">GBP</option>
                  </select>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Duration *</label>
                  <select
                    value={formData.duration}
                    onChange={(e) => setFormData({ ...formData, duration: e.target.value as DiscountDuration })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  >
                    <option value="once">Once</option>
                    <option value="repeating">Repeating</option>
                    <option value="forever">Forever</option>
                  </select>
                </div>

                {formData.duration === 'repeating' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Duration in Months *</label>
                    <input
                      type="number"
                      value={formData.durationInMonths || ''}
                      onChange={(e) => setFormData({ ...formData, durationInMonths: parseInt(e.target.value) || undefined })}
                      min={1}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                    />
                  </div>
                )}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Max Redemptions (optional)</label>
                  <input
                    type="number"
                    value={formData.maxRedemptions || ''}
                    onChange={(e) => setFormData({ ...formData, maxRedemptions: parseInt(e.target.value) || undefined })}
                    min={1}
                    placeholder="Unlimited if empty"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Expires At (optional)</label>
                  <input
                    type="datetime-local"
                    value={formData.expiresAt ? new Date(formData.expiresAt).toISOString().slice(0, 16) : ''}
                    onChange={(e) => {
                      if (e.target.value) {
                        const selectedDate = new Date(e.target.value);
                        const now = new Date();
                        // Ensure date is at least 2 minutes in the future
                        if (selectedDate.getTime() <= now.getTime() + 120000) {
                          toast.error('Expiration date must be at least 2 minutes in the future');
                          return;
                        }
                        setFormData({ ...formData, expiresAt: selectedDate.toISOString() });
                      } else {
                        setFormData({ ...formData, expiresAt: undefined });
                      }
                    }}
                    min={new Date(Date.now() + 120000).toISOString().slice(0, 16)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  />
                  <p className="text-xs text-gray-500 mt-1">Must be at least 2 minutes in the future</p>
                </div>
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="firstTimeOnly"
                  checked={formData.firstTimeTransactionOnly || false}
                  onChange={(e) => setFormData({ ...formData, firstTimeTransactionOnly: e.target.checked })}
                  className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                />
                <label htmlFor="firstTimeOnly" className="ml-2 block text-sm text-gray-700">
                  First-time transaction only
                </label>
              </div>

              <div className="flex items-center bg-yellow-50 p-3 rounded-lg border border-yellow-200">
                <input
                  type="checkbox"
                  id="useTestMode"
                  checked={formData.useTestMode || false}
                  onChange={(e) => setFormData({ ...formData, useTestMode: e.target.checked })}
                  className="h-4 w-4 text-yellow-600 focus:ring-yellow-500 border-gray-300 rounded"
                />
                <label htmlFor="useTestMode" className="ml-2 block text-sm text-gray-700">
                  <span className="font-medium">Use Test Mode</span>
                  <span className="text-xs text-gray-500 block mt-1">
                    Create this discount code in Stripe test mode (for testing with test cards)
                  </span>
                </label>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Minimum Amount (optional, in cents)</label>
                <input
                  type="number"
                  value={formData.minimumAmount || ''}
                  onChange={(e) => setFormData({ ...formData, minimumAmount: parseInt(e.target.value) || undefined })}
                  min={0}
                  placeholder="No minimum"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                />
              </div>
            </div>

            <div className="mt-6 flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowCreateModal(false);
                  resetForm();
                }}
                className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleCreate}
                disabled={isCreating}
                className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50"
              >
                {isCreating ? 'Creating...' : 'Create Code'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && selectedCode && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50" onClick={() => setShowEditModal(false)}>
          <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-lg shadow-lg rounded-md bg-white" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">Edit Discount Code</h3>
              <button
                onClick={() => {
                  setShowEditModal(false);
                  setSelectedCode(null);
                  resetForm();
                }}
                className="text-gray-400 hover:text-gray-500"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            <div className="space-y-4">
              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="text-sm text-gray-600 mb-2">Code: <span className="font-medium text-gray-900">{selectedCode.code}</span></div>
                <div className="text-sm text-gray-600 mb-2">Discount: <span className="font-medium text-gray-900">{formatDiscountValue(selectedCode)}</span></div>
                <div className="text-sm text-gray-600">Usage: <span className="font-medium text-gray-900">{selectedCode.timesRedeemed} / {selectedCode.maxRedemptions || '∞'}</span></div>
              </div>

              <div className="text-sm text-gray-600 mb-2">
                <strong>Note:</strong> Only the "Active" status can be changed. Other fields cannot be modified after creation.
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="editActive"
                  checked={(formData as any).active !== undefined ? (formData as any).active : selectedCode.active}
                  onChange={(e) => setFormData({ ...formData, active: e.target.checked } as any)}
                  className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                />
                <label htmlFor="editActive" className="ml-2 block text-sm text-gray-700">
                  Active
                </label>
              </div>
            </div>

            <div className="mt-6 flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowEditModal(false);
                  setSelectedCode(null);
                  resetForm();
                }}
                className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleUpdate}
                disabled={isUpdating}
                className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50"
              >
                {isUpdating ? 'Updating...' : 'Update Code'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Validation Modal */}
      {showValidationModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50" onClick={() => setShowValidationModal(false)}>
          <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-lg shadow-lg rounded-md bg-white" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">Validate Discount Code</h3>
              <button
                onClick={() => {
                  setShowValidationModal(false);
                  setValidationResult(null);
                  setCodeToValidate('');
                }}
                className="text-gray-400 hover:text-gray-500"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Code to Validate</label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={codeToValidate}
                    onChange={(e) => setCodeToValidate(e.target.value.toUpperCase())}
                    placeholder="Enter code..."
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  />
                  <button
                    onClick={() => handleValidate(codeToValidate)}
                    disabled={isValidating || !codeToValidate}
                    className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50"
                  >
                    {isValidating ? 'Validating...' : 'Validate'}
                  </button>
                </div>
              </div>

              {validationResult && (
                <div className={`p-4 rounded-lg border-2 ${validationResult.valid ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
                  <div className="flex items-start">
                    {validationResult.valid ? (
                      <CheckCircleIcon className="h-6 w-6 text-green-600 mr-3 flex-shrink-0" />
                    ) : (
                      <XCircleIcon className="h-6 w-6 text-red-600 mr-3 flex-shrink-0" />
                    )}
                    <div className="flex-1">
                      <h4 className={`text-sm font-medium mb-2 ${validationResult.valid ? 'text-green-800' : 'text-red-800'}`}>
                        {validationResult.valid ? 'Code is Valid' : 'Code is Invalid'}
                      </h4>
                      {validationResult.message && (
                        <p className={`text-sm mb-3 ${validationResult.valid ? 'text-green-700' : 'text-red-700'}`}>
                          {validationResult.message}
                        </p>
                      )}
                      {validationResult.valid && validationResult.codeDetails && (
                        <div className="space-y-2 text-sm">
                          <div className="grid grid-cols-2 gap-2">
                            <div>
                              <span className="text-gray-600">Original Amount:</span>
                              <span className="ml-2 font-medium text-gray-900">$39.00</span>
                            </div>
                            <div>
                              <span className="text-gray-600">Discount:</span>
                              <span className="ml-2 font-medium text-green-600">
                                -${(validationResult.discountAmount / 100).toFixed(2)}
                              </span>
                            </div>
                            <div className="col-span-2 pt-2 border-t border-green-200">
                              <span className="text-gray-600">Final Amount:</span>
                              <span className="ml-2 font-bold text-lg text-gray-900">
                                ${(validationResult.finalAmount / 100).toFixed(2)}
                              </span>
                            </div>
                          </div>
                          <div className="mt-3 pt-3 border-t border-green-200">
                            <div className="text-xs text-gray-600">
                              <div>Type: {validationResult.codeDetails.discountType === 'percentage' ? 'Percentage' : 'Fixed Amount'}</div>
                              <div>Value: {validationResult.codeDetails.discountType === 'percentage' 
                                ? `${validationResult.codeDetails.discountValue}%` 
                                : `$${(validationResult.codeDetails.discountValue / 100).toFixed(2)}`}
                              </div>
                              <div>Usage: {validationResult.codeDetails.timesRedeemed} / {validationResult.codeDetails.maxRedemptions || '∞'}</div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <p className="text-xs text-blue-800">
                  <strong>Note:</strong> This validation tests the code with a $39.00 purchase (one seat). 
                  The code is not redeemed during this validation.
                </p>
              </div>
            </div>

            <div className="mt-6 flex justify-end">
              <button
                onClick={() => {
                  setShowValidationModal(false);
                  setValidationResult(null);
                  setCodeToValidate('');
                }}
                className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reactivate Modal */}
      {showReactivateModal && codeToReactivate && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50" onClick={() => setShowReactivateModal(false)}>
          <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-lg shadow-lg rounded-md bg-white" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">Reactivate Discount Code</h3>
              <button
                onClick={() => {
                  setShowReactivateModal(false);
                  setCodeToReactivate(null);
                  setNewExpirationDate('');
                }}
                className="text-gray-400 hover:text-gray-500"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            <div className="space-y-4">
              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="text-sm text-gray-600 mb-2">Code: <span className="font-medium text-gray-900">{codeToReactivate.code}</span></div>
                <div className="text-sm text-gray-600 mb-2">
                  Current Expiration: <span className="font-medium text-gray-900">
                    {codeToReactivate.expiresAt 
                      ? formatDateTimeEST(codeToReactivate.expiresAt)
                      : 'No expiration date'}
                  </span>
                </div>
                <div className="text-xs text-blue-600 mt-2">
                  ℹ️ A new promotion code will be created with the same configuration and a new expiration date. The old code will be deactivated.
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Extend by Days
                  </label>
                  <select
                    value={(() => {
                      if (!newExpirationDate) return '1';
                      const selected = new Date(newExpirationDate);
                      const today = new Date();
                      today.setHours(0, 0, 0, 0);
                      selected.setHours(0, 0, 0, 0);
                      const diffDays = Math.ceil((selected.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
                      if (diffDays === 1) return '1';
                      if (diffDays === 7) return '7';
                      if (diffDays === 30) return '30';
                      if (diffDays === 90) return '90';
                      return '';
                    })()}
                    onChange={(e) => {
                      if (e.target.value) {
                        const days = parseInt(e.target.value);
                        const newDate = new Date();
                        newDate.setDate(newDate.getDate() + days);
                        newDate.setHours(23, 59, 0, 0); // Set to end of day
                        setNewExpirationDate(newDate.toISOString().slice(0, 16));
                      } else {
                        // Keep current date if switching to custom
                        if (!newExpirationDate) {
                          const tomorrow = new Date();
                          tomorrow.setDate(tomorrow.getDate() + 1);
                          tomorrow.setHours(23, 59, 0, 0);
                          setNewExpirationDate(tomorrow.toISOString().slice(0, 16));
                        }
                      }
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  >
                    <option value="1">1 day (default)</option>
                    <option value="7">7 days</option>
                    <option value="30">30 days</option>
                    <option value="90">90 days</option>
                    <option value="">Custom date</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Or Custom Date
                  </label>
                  <input
                    type="datetime-local"
                    value={newExpirationDate}
                    onChange={(e) => setNewExpirationDate(e.target.value)}
                    min={new Date(Date.now() + 120000).toISOString().slice(0, 16)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  />
                </div>
              </div>

              {newExpirationDate && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                  <p className="text-xs text-green-800">
                    <strong>New expiration date:</strong> {new Date(newExpirationDate).toLocaleDateString('en-US', { 
                      month: 'long', 
                      day: 'numeric', 
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </p>
                </div>
              )}

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <p className="text-xs text-blue-800">
                  <strong>What happens when you reactivate:</strong>
                  <br />• A new promotion code will be created with the same code name and configuration
                  <br />• The new code will have the expiration date you specify (default: tomorrow)
                  <br />• The old code will be deactivated
                  <br />• The new code will be active and ready to use
                </p>
              </div>
            </div>

            <div className="mt-6 flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowReactivateModal(false);
                  setCodeToReactivate(null);
                  setNewExpirationDate('');
                }}
                className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  if (!codeToReactivate) {
                    toast.error('Code information is missing. Please try again.');
                    return;
                  }
                  
                  try {
                    // Default to 1 day from now if no date specified
                    let expiresAt = newExpirationDate;
                    if (!expiresAt) {
                      const tomorrow = new Date();
                      tomorrow.setDate(tomorrow.getDate() + 1);
                      tomorrow.setHours(23, 59, 0, 0); // End of day
                      expiresAt = tomorrow.toISOString();
                    } else {
                      // Ensure the date is a full ISO string
                      const date = new Date(expiresAt);
                      if (isNaN(date.getTime())) {
                        toast.error('Invalid date format. Please select a valid date.');
                        return;
                      }
                      
                      // Validate date is in the future
                      const now = new Date();
                      if (date <= now) {
                        toast.error('Expiration date must be in the future.');
                        return;
                      }
                      
                      expiresAt = date.toISOString();
                    }

                    const result = await reactivateCode({
                      id: codeToReactivate.id,
                      newExpiresAt: expiresAt,
                    }).unwrap();
                    
                    if (!result) {
                      toast.error('Failed to reactivate code. Please try again.');
                      return;
                    }
                    
                    const newExpDate = result.expiresAt 
                      ? (() => {
                          const expDate = new Date(result.expiresAt);
                          if (isNaN(expDate.getTime())) {
                            return 'Invalid date';
                          }
                          return expDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' });
                        })()
                      : 'never';
                    toast.success(`Code "${result.code || codeToReactivate.code}" has been reactivated! New expiration: ${newExpDate}`);
                    setShowReactivateModal(false);
                    setCodeToReactivate(null);
                    setNewExpirationDate('');
                    refetch();
                  } catch (error: any) {
                    const errorMsg = error?.data?.error || error?.data?.message || error?.message || 'Failed to reactivate code';
                    toast.error(errorMsg);
                  }
                }}
                disabled={isReactivating || !codeToReactivate}
                className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700 disabled:opacity-50"
              >
                {isReactivating ? 'Creating New Code...' : 'Reactivate with New Date'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Component for expanded code stats row
const CodeStatsRow: React.FC<{ codeId: string; code: DiscountCode }> = ({ codeId, code }) => {
  const { data: statsData, isLoading } = useGetDiscountCodeUsageStatsQuery(codeId);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-4">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  const stats = statsData?.data;

  if (!stats) {
    return <div className="text-sm text-gray-500 py-2">No statistics available</div>;
  }

  // Get tracked redemptions from code object
  const trackedRedemptions = code.trackedRedemptionsList || [];

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 py-4">
      {/* Usage Statistics */}
      <div>
        <h4 className="text-sm font-semibold text-gray-900 mb-3">Usage Statistics</h4>
        <div className="space-y-2">
          <div className="flex justify-between text-sm">
            <span className="text-gray-600">Total Redemptions (Stripe):</span>
            <span className="font-semibold text-gray-900">{stats.totalRedemptions}</span>
          </div>
          {code.trackedRedemptions !== undefined && (
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Tracked Redemptions:</span>
              <span className="font-semibold text-blue-600">{code.trackedRedemptions}</span>
            </div>
          )}
          {stats.maxRedemptions && (
            <>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Remaining:</span>
                <span className="font-semibold text-green-600">{stats.remainingRedemptions}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Usage:</span>
                <span className="font-semibold text-gray-900">{stats.usagePercentage}%</span>
              </div>
            </>
          )}
          <div className="flex justify-between text-sm">
            <span className="text-gray-600">Type:</span>
            <span className="font-semibold text-gray-900">
              {stats.isSingleUse ? 'Single Use' : 'Multiple Use'}
            </span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-gray-600">Status:</span>
            <span className={`font-semibold ${
              stats.status === 'active' ? 'text-green-600' :
              stats.status === 'expired' ? 'text-red-600' :
              stats.status === 'max_uses_reached' ? 'text-orange-600' :
              'text-gray-600'
            }`}>
              {stats.status.replace('_', ' ').toUpperCase()}
            </span>
          </div>
        </div>
      </div>

      {/* Financial Impact */}
      <div>
        <h4 className="text-sm font-semibold text-gray-900 mb-3">Financial Impact</h4>
        <div className="space-y-2">
          <div className="flex justify-between text-sm">
            <span className="text-gray-600">Total Discount Given:</span>
            <span className="font-semibold text-green-600">
              ${(stats.totalDiscountGiven / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-gray-600">Avg. Discount/Use:</span>
            <span className="font-semibold text-gray-900">
              ${(stats.averageDiscountPerRedemption / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </span>
          </div>
          {stats.isFullyUsed && (
            <div className="mt-2 p-2 bg-orange-50 rounded text-xs text-orange-800">
              ⚠️ This code has reached its maximum uses
            </div>
          )}
        </div>
      </div>

      {/* Tracked Redemptions List */}
      {trackedRedemptions.length > 0 ? (
        <div>
          <h4 className="text-sm font-semibold text-gray-900 mb-3">Tracked Redemptions ({trackedRedemptions.length})</h4>
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {trackedRedemptions.map((redemption) => (
              <div key={redemption.id} className="border border-gray-200 rounded p-2 text-xs">
                <div className="flex justify-between mb-1">
                  <span className="font-medium text-gray-900">
                    {redemption.userEmail || redemption.customerId || `User ID: ${redemption.userId || 'N/A'}`}
                  </span>
                  <span className="text-gray-500">
                    {new Date(redemption.redeemedAt).toLocaleDateString('en-US', {
                      month: 'short',
                      day: 'numeric',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </span>
                </div>
                {redemption.invoiceId && (
                  <div className="text-gray-500">
                    Invoice: {redemption.invoiceId}
                  </div>
                )}
                {redemption.paymentIntentId && (
                  <div className="text-gray-500">
                    Payment: {redemption.paymentIntentId.substring(0, 20)}...
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div>
          <h4 className="text-sm font-semibold text-gray-900 mb-3">Code Details</h4>
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Code:</span>
              <span className="font-mono font-semibold text-gray-900">{stats.code}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Created:</span>
              <span className="text-gray-900">
                {new Date(code.createdAt).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'short',
                  day: 'numeric',
                })}
              </span>
            </div>
            {code.expiresAt && (
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Expires:</span>
                <span className="text-gray-900">
                  {new Date(code.expiresAt).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'short',
                    day: 'numeric',
                  })}
                </span>
              </div>
            )}
            {stats.isSingleUse && stats.totalRedemptions > 0 && (
              <div className="mt-2 p-2 bg-green-50 rounded text-xs text-green-800">
                ✅ This single-use code has been redeemed
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default DiscountCodes;
